import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiMapPin, FiMail, FiPhone, FiCalendar, 
  FiTrendingUp, FiAward, FiPlay, FiBookOpen, 
  FiUsers, FiUser, FiDownload, FiCheckCircle, 
  FiYoutube, FiInstagram, FiTwitter, FiLoader
} = FiIcons;

function PublicResume() {
  const { athleteId } = useParams();
  const [downloading, setDownloading] = useState(false);

  // Mock data for the Demo Resume
  const profile = {
    first_name: 'Alex',
    last_name: 'Rodriguez',
    sport: 'Basketball',
    position: 'Point Guard',
    graduation_year: '2025',
    city: 'Los Angeles',
    state: 'CA',
    email: 'alex.rodriguez@example.com',
    phone: '(555) 123-4567',
    profile_image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=800&h=800&fit=crop&crop=face',
    height: '6\'2"',
    weight: '185',
    gpa: '3.8',
    sat_score: '1450',
    act_score: '32',
    class_rank: '15/450',
    high_school: 'Lincoln High School',
    club_team: 'LA Elite Basketball',
    league: 'CIF Southern Section',
    division: 'Division I',
    youtube_channel: 'https://youtube.com/@alexrodriguezhoops',
    username: 'alexrodriguez',
    instagram: 'alexrodriguez',
    twitter: 'alexrodriguez',
    philosophy_quote: "I strive to be the hardest worker in the room and lead by example on and off the court.",
    privacy_settings: {
      showContactInfo: true,
      showAcademicRecords: true,
      showAcademicHonors: true,
      showAdvancedCourses: true,
      showTeamInfo: true,
      showPhysicalStats: true,
      showPerformanceStats: true,
      showVideoContent: true,
      showSocialMedia: true
    }
  };

  const honors = [
    { honor: 'Honor Roll (2022-2024)' },
    { honor: 'National Honor Society' },
    { honor: 'AP Scholar with Distinction' }
  ];

  const courses = [
    { course: 'AP Calculus AB' },
    { course: 'AP English Literature' },
    { course: 'AP US History' },
    { course: 'Honors Chemistry' }
  ];

  const stats = [
    { id: 1, stat_name: 'Points per Game', stat_value: '24.5', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 2, stat_name: 'Assists per Game', stat_value: '8.2', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 3, stat_name: 'Rebounds per Game', stat_value: '5.1', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 4, stat_name: 'Field Goal %', stat_value: '52%', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 5, stat_name: 'Free Throw %', stat_value: '88%', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 6, stat_name: 'Steals per Game', stat_value: '2.1', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 7, stat_name: '3PT %', stat_value: '41%', season: '2023-24', created_at: '2024-01-15T00:00:00Z' },
    { id: 8, stat_name: 'Turnovers', stat_value: '1.2', season: '2023-24', created_at: '2024-01-15T00:00:00Z' }
  ];

  const achievements = [
    { id: 1, achievement: 'All-State First Team', year: '2024' },
    { id: 2, achievement: 'Regional MVP', year: '2023' },
    { id: 3, achievement: 'Team Captain', year: '2023' },
    { id: 4, achievement: 'CIF Southern Section Champion', year: '2023' }
  ];

  const videos = [
    { id: 1, title: 'Season Highlights 2024', thumbnail: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&h=300&fit=crop', duration: '3:45', url: '#' },
    { id: 2, title: 'Championship Game Performance', thumbnail: 'https://images.unsplash.com/photo-1574623452334-1e0ac2b3ccb4?w=400&h=300&fit=crop', duration: '2:30', url: '#' }
  ];

  const downloadPDF = () => {
    alert('This is a demo resume. PDF downloads are available on live student-athlete profiles!');
  };

  const privacySettings = profile.privacy_settings;
  const showContactInfo = privacySettings.showContactInfo;
  const showAcademicRecords = privacySettings.showAcademicRecords;
  const showAcademicHonors = privacySettings.showAcademicHonors;
  const showTeamInfo = privacySettings.showTeamInfo;
  const showPerformanceStats = privacySettings.showPerformanceStats;
  const showVideoContent = privacySettings.showVideoContent;

  let displayLocation = '';
  if (profile.city && profile.state) displayLocation = `${profile.city}, ${profile.state}`;
  else if (profile.city || profile.state) displayLocation = profile.city || profile.state;
  else if (profile.location) displayLocation = profile.location;

  // Reusable Sidebar Icon Component
  const SidebarIcon = ({ icon, label }) => (
    <div className="flex flex-col items-center justify-center space-y-2 text-slate-400 hover:text-white transition-colors cursor-pointer group">
      <SafeIcon icon={icon} className="w-6 h-6 group-hover:scale-110 transition-transform" />
      <span className="text-[10px] font-bold tracking-widest uppercase">{label}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-4 md:py-12 px-4 sm:px-6 lg:px-8 flex justify-center font-inter">
      {/* Demo Banner */}
      <div className="fixed top-0 left-0 right-0 bg-primary text-white text-center py-2 text-sm font-bold z-50 shadow-md">
        DEMO MODE: This is an example of a public athlete resume.
      </div>

      {/* Main Container - The "Magazine/Tablet" Frame */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-[1400px] bg-white rounded-2xl md:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row mt-6"
      >
        
        {/* Left Sidebar (Desktop Only) */}
        <div className="hidden lg:flex flex-col items-center w-28 bg-[#0A192F] py-10 border-r border-slate-700/50 z-10 shrink-0">
          <div className="w-14 h-14 bg-slate-800 rounded-xl flex items-center justify-center mb-16 shadow-lg border border-slate-700">
            <span className="text-[#D4AF37] font-black text-2xl">N</span>
          </div>
          <div className="flex flex-col space-y-10 w-full">
            <SidebarIcon icon={FiUser} label="PROFILE" />
            <SidebarIcon icon={FiTrendingUp} label="STATS" />
            <SidebarIcon icon={FiAward} label="AWARDS" />
            <SidebarIcon icon={FiBookOpen} label="ACADEMICS" />
            <SidebarIcon icon={FiMail} label="CONTACT" />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col">
          
          {/* Top Hero Section */}
          <div className="flex flex-col md:flex-row bg-white">
            {/* Athlete Photo */}
            <div className="w-full md:w-2/5 relative h-[400px] md:h-auto bg-[#0A192F] overflow-hidden">
              {profile.profile_image ? (
                <img 
                  src={profile.profile_image} 
                  alt={`${profile.first_name} ${profile.last_name}`} 
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
                  <SafeIcon icon={FiUser} className="w-24 h-24 text-slate-600" />
                </div>
              )}
              {/* Subtle gradient for text readability if stacked on mobile */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 to-transparent md:hidden" />
            </div>

            {/* Athlete Key Info */}
            <div className="w-full md:w-3/5 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative -mt-20 md:mt-0 z-10 bg-white md:bg-transparent rounded-t-3xl md:rounded-none">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[#D4AF37] text-xs md:text-sm font-bold tracking-[0.2em] mb-2 uppercase">
                    Student Athlete
                  </p>
                  <h1 className="text-4xl md:text-5xl lg:text-7xl font-space-grotesk font-black text-[#0A192F] uppercase leading-[1.1] tracking-tight">
                    {profile.first_name} <br /> {profile.last_name}
                  </h1>
                  <p className="text-slate-500 font-bold tracking-[0.15em] mt-4 uppercase text-xs md:text-sm">
                    {profile.position || 'Athlete'} &nbsp;|&nbsp; {profile.height || '--'} &nbsp;|&nbsp; {profile.weight ? `${profile.weight} LBS` : '--'}
                  </p>
                </div>
                
                {/* Download Button */}
                <button 
                  onClick={downloadPDF}
                  disabled={downloading}
                  title="Download PDF"
                  className="hidden sm:flex bg-[#0A192F] hover:bg-slate-800 text-white p-4 rounded-full shadow-xl transition-transform hover:scale-105 disabled:opacity-50"
                >
                  <SafeIcon icon={downloading ? FiLoader : FiDownload} className={`w-5 h-5 ${downloading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <hr className="my-8 md:my-10 border-slate-200" />

              {/* 3 Icons Quick Info Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center mb-3">
                    <SafeIcon icon={FiAward} className="w-5 h-5 text-[#0A192F]" />
                  </div>
                  <span className="text-[10px] md:text-xs font-bold text-[#0A192F] uppercase tracking-wider">{profile.sport || 'Sport'}</span>
                  <span className="text-[10px] md:text-xs text-slate-500 uppercase mt-1">{profile.division || 'Varsity'}</span>
                </div>
                
                <div className="flex flex-col items-center text-center border-l border-r border-slate-200">
                  <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center mb-3">
                    <SafeIcon icon={FiCalendar} className="w-5 h-5 text-[#0A192F]" />
                  </div>
                  <span className="text-[10px] md:text-xs font-bold text-[#0A192F] uppercase tracking-wider">Class Of</span>
                  <span className="text-[10px] md:text-xs text-slate-500 uppercase mt-1">{profile.graduation_year || '--'}</span>
                </div>

                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center mb-3">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 text-[#0A192F]" />
                  </div>
                  <span className="text-[10px] md:text-xs font-bold text-[#0A192F] uppercase tracking-wider truncate w-full px-2">
                    {profile.high_school || 'High School'}
                  </span>
                  <span className="text-[10px] md:text-xs text-slate-500 uppercase mt-1 truncate w-full px-2">
                    {displayLocation || '--'}
                  </span>
                </div>
              </div>
              
              {/* Mobile Download Button */}
              <button 
                onClick={downloadPDF}
                disabled={downloading}
                className="mt-8 w-full sm:hidden bg-[#0A192F] text-white py-3 rounded-lg font-bold tracking-widest text-xs uppercase flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={downloading ? FiLoader : FiDownload} className={`w-4 h-4 ${downloading ? 'animate-spin' : ''}`} />
                <span>{downloading ? 'Generating...' : 'Download PDF'}</span>
              </button>
            </div>
          </div>

          {/* Middle Banner (Dark Navy) - Highlights & Stats */}
          <div className="bg-[#0A192F] text-white p-8 md:p-12 lg:p-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Athletic Highlights */}
            <div>
              <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] mb-6 uppercase">Athletic Highlights</p>
              {achievements.length > 0 ? (
                <ul className="space-y-4">
                  {achievements.slice(0, 5).map((achievement) => (
                    <li key={achievement.id} className="flex items-start space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm md:text-base text-slate-200 font-medium">
                        {achievement.achievement}{achievement.year && ` (${achievement.year})`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-slate-500 text-sm italic">No athletic highlights added yet.</p>
              )}
            </div>

            {/* Season Stats */}
            {showPerformanceStats && (
              <div>
                <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] mb-6 uppercase">Season Stats</p>
                {stats.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 md:gap-8">
                    {stats.slice(0, 8).map((stat) => (
                      <div key={stat.id} className="flex flex-col">
                        <span className="text-3xl md:text-4xl lg:text-5xl font-space-grotesk font-black text-white mb-1 leading-none tracking-tighter">
                          {stat.stat_value}
                        </span>
                        <span className="text-[10px] md:text-xs text-slate-400 font-bold tracking-[0.2em] uppercase leading-tight">
                          {stat.stat_name}
                        </span>
                        <span className="text-[9px] md:text-[10px] text-[#D4AF37] font-medium mt-1 uppercase opacity-90">
                          {stat.season ? stat.season : new Date(stat.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm italic">No season stats added yet.</p>
                )}
              </div>
            )}
          </div>

          {/* Bottom Section - Academics, Team/Leadership, Video */}
          <div className="bg-white p-8 md:p-12 lg:p-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            
            {/* Academics */}
            {showAcademicRecords && (
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-[#0A192F]" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#0A192F]">Academics</h3>
                </div>
                
                <div className="flex space-x-8 mb-8 pb-8 border-b border-slate-100">
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">GPA</p>
                    <p className="text-3xl font-space-grotesk font-black text-[#0A192F]">{profile.gpa || '--'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">SAT</p>
                    <p className="text-3xl font-space-grotesk font-black text-[#0A192F]">{profile.sat_score || '--'}</p>
                  </div>
                  {profile.act_score && (
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">ACT</p>
                      <p className="text-3xl font-space-grotesk font-black text-[#0A192F]">{profile.act_score}</p>
                    </div>
                  )}
                </div>

                {showAcademicHonors && honors.length > 0 && (
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-4">Honors</p>
                    <ul className="space-y-3">
                      {honors.slice(0, 4).map((item, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-[#0A192F] font-medium">
                          <span className="text-[#D4AF37] mt-0.5">•</span>
                          <span>{item.honor}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Team / Program */}
            {showTeamInfo && (
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <SafeIcon icon={FiUsers} className="w-5 h-5 text-[#0A192F]" />
                  <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#0A192F]">Team & Program</h3>
                </div>
                
                <div className="space-y-6">
                  {profile.club_team && (
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">Club / Travel Team</p>
                      <p className="text-sm font-bold text-[#0A192F]">{profile.club_team}</p>
                    </div>
                  )}
                  {profile.league && (
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">League</p>
                      <p className="text-sm font-bold text-[#0A192F]">{profile.league}</p>
                    </div>
                  )}
                  {profile.division && (
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-1">Division</p>
                      <p className="text-sm font-bold text-[#0A192F]">{profile.division}</p>
                    </div>
                  )}

                  {/* Quote / Dedication */}
                  {profile.philosophy_quote && (
                    <div className="pt-6 mt-6 border-t border-slate-100">
                      <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-4">Philosophy</p>
                      <blockquote className="text-sm italic text-slate-600 border-l-2 border-[#D4AF37] pl-4">
                        "{profile.philosophy_quote}"
                      </blockquote>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Video Highlights & Contact */}
            <div className="flex flex-col justify-between">
              {showVideoContent && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <SafeIcon icon={FiPlay} className="w-5 h-5 text-[#0A192F]" />
                      <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-[#0A192F]">Film Room</h3>
                    </div>
                    {profile.youtube_channel && (
                      <a href={profile.youtube_channel} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700">
                        <SafeIcon icon={FiYoutube} className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                  
                  {videos.length > 0 ? (
                    <div className="space-y-4">
                      {videos.slice(0, 2).map((video) => (
                        <a key={video.id} href={video.url} target="_blank" rel="noopener noreferrer" className="group block relative rounded-xl overflow-hidden shadow-sm">
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-32 bg-slate-200 flex items-center justify-center">
                              <SafeIcon icon={FiPlay} className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-[#0A192F]/40 group-hover:bg-[#0A192F]/20 transition-colors flex items-center justify-center">
                            <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
                              <SafeIcon icon={FiPlay} className="w-5 h-5 text-[#0A192F] ml-1" />
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                            <p className="text-white text-xs font-bold truncate">{video.title}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm italic">No videos added yet.</p>
                  )}
                </div>
              )}

              {/* Contact Block */}
              {showContactInfo && (profile.email || profile.phone) && (
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 mt-auto">
                  <p className="text-[10px] text-slate-400 font-bold tracking-[0.2em] uppercase mb-4">Direct Contact</p>
                  {profile.email && (
                    <a href={`mailto:${profile.email}`} className="flex items-center space-x-3 text-sm font-medium text-[#0A192F] hover:text-[#D4AF37] transition-colors mb-3">
                      <SafeIcon icon={FiMail} className="w-4 h-4 text-slate-400" />
                      <span className="truncate">{profile.email}</span>
                    </a>
                  )}
                  {profile.phone && (
                    <a href={`tel:${profile.phone}`} className="flex items-center space-x-3 text-sm font-medium text-[#0A192F] hover:text-[#D4AF37] transition-colors">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 text-slate-400" />
                      <span>{profile.phone}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-[#050A13] text-slate-400 p-6 md:p-8 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8 text-xs font-bold tracking-widest uppercase rounded-b-2xl md:rounded-b-[2rem]">
            {profile.email && (
              <a href={`mailto:${profile.email}`} className="flex items-center space-x-2 hover:text-white transition-colors">
                <SafeIcon icon={FiMail} className="w-4 h-4" />
                <span>{profile.email}</span>
              </a>
            )}
            <div className="flex items-center space-x-6">
              <span className="hover:text-white transition-colors cursor-pointer flex items-center space-x-2">
                <SafeIcon icon={FiInstagram} className="w-4 h-4" />
                <span>@{profile.username}</span>
              </span>
              <span className="hover:text-white transition-colors cursor-pointer flex items-center space-x-2">
                <SafeIcon icon={FiTwitter} className="w-4 h-4" />
                <span>@{profile.username}</span>
              </span>
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

export default PublicResume;